import type { Move } from '../../../schemas/move';

/**
 * Mosse iniziali del Chierico (manuale V1, rr. 2680-2757).
 *
 * - **4 mosse automatiche** (`type: 'starting'`): *Lanciare Incantesimi
 *   Divini*, *Mana*, *Divinità*, *Assistenza Divina*. Tutte concesse
 *   automaticamente al livello 1.
 * - **3 mosse di scelta** (`pickCount: 1`): *La Bilancia della Vita e della
 *   Morte*, *Senti il Popolo Cantare?*, *Scacciare Non Morti* (manuale
 *   r. 2745: "Inoltre, scegli una delle seguenti mosse").
 *
 * **Pattern notabili**:
 * - *Lanciare Incantesimi Divini* è la mossa di casting (`roll.stat: 'saggezza'`).
 *   In `services/spellAccess.ts` è il trigger per l'attivazione della
 *   spell-list `chierico-native`.
 * - *Mana*: cross-class restriction in description ("Se hai già preso
 *   questa mossa come Mago o come Warlock, non osare prenderla una seconda
 *   volta!").
 * - **Divinità**: mossa-config con scelta della **Tradizione Sacra**
 *   (5 opzioni, `choiceCount: 1`). Il `MoveRef.selectedChoices` di questa
 *   mossa è l'SSOT della Tradizione del PG. Sfera d'Influenza, Fondamento
 *   e Nome divinità rimangono in description (workaround D2 della PR —
 *   FI-18 li strutturerà in PR dedicata con `MoveSchema.choiceGroups`).
 */

const lanciareIncantesimiDivini: Move = {
  id: 'chierico.lanciare-incantesimi-divini',
  name: 'Lanciare Incantesimi Divini',
  type: 'starting',
  classId: 'chierico',
  trigger: 'Quando vuoi lanciare un incantesimo che fa parte della tua Tradizione Sacra',
  description:
    "Se si tratta di un'orazione, non importa che tiri: la lanci e basta. Altrimenti, se il tuo livello da Chierico e le tue prese di *Mana* residue sono almeno pari al livello dell'incantesimo che vuoi lanciare, tira+**SAG** (nota che le prese di *Mana* le stai solo rischiando, non spendendo!). Con un 10+, l'incantesimo viene lanciato con successo. Con 7-9, lo stesso, ma devi anche scegliere un'opzione:\n\n- attiri attenzioni indesiderate, ti esponi al pericolo o metti te stesso in difficoltà. Il GM ti dirà come;\n- il lancio ti allontana dal tuo dio: prendi -1 continuato a *Lanciare Incantesimi Divini* fino a quando non usi nuovamente la mossa *Mana*. Se scegli più volte quest'opzione, il malus si somma;\n- spendi prese di *Mana* pari al livello dell'incantesimo lanciato.\n\nRicorda che mantenere attivo un incantesimo con effetti continuati potrebbe causare una penalità ai tuoi tiri di *lanciare incantesimi*.\n\nSe prendi questa mossa con una mossa multiclasse, ottieni anche *Mana* e *Divinità*.",
  roll: {
    stat: 'saggezza',
    on10plus: { text: "L'incantesimo viene lanciato con successo." },
    on7to9: {
      text: "Lanci comunque l'incantesimo, ma scegli un'opzione: attenzioni indesiderate / -1 continuato fino a prossimo Mana / spendi prese di Mana pari al livello dell'incantesimo.",
    },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
  // **FI-36 (PR-A2 Step 5a)**: spell-grant setup-L1 della linea Divina,
  // simmetricamente a `mago.lanciare-incantesimi-arcani` e
  // `warlock.lanciare-incantesimi-del-patrono`. Una sola dichiarazione
  // qui copre Chierico + Druido + Paladino-Sacro (i due ultimi acquisiscono
  // LIDV via bundle cross-class — vedi `druido.divinita-della-natura`
  // r. 158 e `paladino.ordine-sacro` r. 319). Le 2 grant `all-of-level`
  // sono di famiglia "auto" (niente scelta del giocatore) — il pool
  // calcolato lato consumer applica anche il filtro Tradizione/archetypeId
  // (Chierico: Tradizione da `chierico.divinita.selectedChoiceGroups`;
  // Druido: Tradizione "natura" implicita; Paladino: Tradizione da
  // `paladino.ordine-sacro.selectedChoices` + archetypeId 'elfo' per natura;
  // Voce della Terra L0 richiede archetipo Nano via `requiresArchetype`).
  //  - Grant 1: tutte le orazioni L0 della Tradizione auto-aggiunte
  //  - Grant 2: tutti gli incantesimi L1 della Tradizione auto-aggiunti
  grantsSpells: [
    {
      trigger: 'on-take',
      levelRule: { kind: 'all-of-level', level: 0 },
      source: { kind: 'own-class' },
    },
    {
      trigger: 'on-take',
      levelRule: { kind: 'all-of-level', level: 1 },
      source: { kind: 'own-class' },
    },
  ],
  // **D7-bundle (FI-17 follow-up)**: bundle all-inclusive del Chierico
  // (manuale V1 rr. 1764-1773: «*Divinità*, *Lanciare Incantesimi Divini*
  // e *Mana*» citato come esempio prototipo). Anchor è la mossa di
  // casting (decisione D7-bundle-Q1). Mana dedupato cross-class via
  // tag `'mana'` (D7-bundle-Q2).
  bundles: ['chierico.divinita', 'chierico.mana'],
};

const mana: Move = {
  id: 'chierico.mana',
  name: 'Mana',
  type: 'starting',
  classId: 'chierico',
  trigger:
    'Quando ti *accampi* e ripassi gli incantesimi che conosci, preghi la tua divinità o rinnovi i reciproci accordi con il tuo Patrono',
  description:
    'Al risveglio perdi tutte le prese di *Mana* che hai e ne prendi un numero pari al tuo più alto livello da Incantatore +1 (massimo 11).\n\nIl *Mana* è uno solo: se hai già preso questa mossa come Mago o come Warlock, non puoi prenderla di nuovo!',
  // **D7-bundle (FI-17 follow-up)**: tag `'mana'` data-driven per dedup
  // cross-class (manuale V1 r. 1133: «Mana è una riserva unica»).
  tags: ['mana'],
  prerequisites: [],
  replaces: [],
};

const divinita: Move = {
  id: 'chierico.divinita',
  name: 'Divinità',
  type: 'starting',
  classId: 'chierico',
  trigger: 'Alla creazione del personaggio (configurazione del culto)',
  description:
    "Scegli la **Sfera d'Influenza** della tua divinità, un **Fondamento** per la tua religione e la tua **Tradizione Sacra** — l'insieme di incantesimi che manifesta il potere della tua divinità. Indica anche il nome della divinità.\n\n*Non ottieni* questa mossa se hai già *Divinità della Natura* (Druido) o *Ordine Sacro* (Paladino).",
  tags: [],
  prerequisites: [],
  replaces: [],
  // FI-18 Fase 2: migrata da `choices` (Tradizione) + description-only
  // (Sfera, Fondamento) a 3 `choiceGroups` strutturati. La Tradizione Sacra
  // vive nel primo gruppo `'tradizione-sacra'` ed è letta da `spellAccess.ts`
  // per attivare la spell-list `chierico-native`.
  choiceGroups: [
    {
      id: 'tradizione-sacra',
      label: 'Tradizione Sacra',
      description: "L'insieme di incantesimi che manifesta il potere della tua divinità.",
      options: [
        {
          id: 'crepuscolo',
          label: 'Crepuscolo',
          description: 'Inganno, furtività, dominio mentale, tenebre.',
        },
        { id: 'guarigione', label: 'Guarigione', description: 'Cura, protezione, ristoro.' },
        {
          id: 'illuminazione',
          label: 'Illuminazione',
          description: 'Divinazione, luce, intuizione.',
        },
        {
          id: 'negromanzia',
          label: 'Negromanzia',
          description: "Manipolazione della vita e della morte, l'aldilà.",
        },
        {
          id: 'valore',
          label: 'Valore',
          description: 'Combattimento, difesa, ispirazione, forza divina.',
        },
      ],
      pickCount: 1,
    },
    {
      id: 'sfera',
      label: "Sfera d'Influenza",
      description: 'A quali ambiti del mondo terreno è interessata la tua divinità?',
      options: [
        { id: 'guarigione-ricostruzione', label: 'Guarigione e Ricostruzione' },
        { id: 'guerra-dominio', label: 'Guerra e Dominio' },
        { id: 'progresso-cultura', label: 'Progresso e Cultura' },
        { id: 'conoscenza-cose-nascoste', label: 'Conoscenza e Cose Nascoste' },
        { id: 'reietti-dimenticati', label: 'i Reietti e i Dimenticati' },
        { id: 'inganno-tradimento', label: 'Inganno e Tradimento' },
        { id: 'prosperita-trionfo', label: 'Prosperità e Trionfo' },
      ],
      pickCount: 1,
    },
    {
      id: 'fondamento',
      label: 'Fondamento della religione',
      description: 'Come pratichi la devozione verso la tua divinità?',
      options: [
        {
          id: 'sacrificio-se',
          label: 'Sacrificare sé stesso o offrire la propria sofferenza',
          description: 'Corpo, mente o spirito.',
        },
        {
          id: 'predicare',
          label: 'Predicare la parola secondo la volontà divina',
        },
        {
          id: 'voti-rinuncia',
          label: 'Imporsi e mantenere voti di rinuncia',
        },
        {
          id: 'sacrificio-altri',
          label: 'Sacrificare il corpo o l’anima di altri esseri viventi',
          description: 'O loro simulacri.',
        },
        {
          id: 'benevolenza-pieta',
          label: 'Farsi portatore della benevolenza o della pietà divina',
        },
        {
          id: 'collera-vendetta',
          label: 'Farsi portatore della collera o della vendetta divina',
        },
        {
          id: 'rituali-liturgici',
          label: 'Compiere complessi rituali liturgici',
        },
        {
          id: 'sacrificio-beni',
          label: 'Sacrificare preziosi beni materiali o farne dono al culto',
        },
      ],
      pickCount: 1,
    },
  ],
  narrativeFields: [
    {
      id: 'nome-divinita',
      label: 'Nome della divinità',
      placeholder: 'es. Helferth, Sucellus, Zorica…',
      required: true,
      multiline: false,
    },
  ],
};

const assistenzaDivina: Move = {
  id: 'chierico.assistenza-divina',
  name: 'Assistenza Divina',
  type: 'starting',
  classId: 'chierico',
  trigger:
    'Quando preghi la divinità in accordo con il Fondamento della tua religione per manifestare un effetto legato alle tue Tradizioni Sacre',
  description:
    "Spiega ciò che speri di ottenere e cosa offri in cambio. Se la preghiera si allinea con il disegno divino (a discrezione del GM, che stabilirà anche un livello di incantesimo per l'effetto), tira come per *Lanciare Incantesimi Divini*. Con un 10+, ciò che desideri si avvera in cambio della tua offerta. Con 7-9, il dio pretende di più o esaudisce a modo suo la tua richiesta (il GM ti dirà cosa o come).\n\nSe prendi questa mossa con una multiclasse, ottieni anche *Divinità*.",
  tags: [],
  prerequisites: ['chierico.lanciare-incantesimi-divini'],
  replaces: [],
};

const laBilanciaDellaVitaEDellaMorte: Move = {
  id: 'chierico.la-bilancia-della-vita-e-della-morte',
  name: 'La Bilancia della Vita e della Morte',
  type: 'starting',
  classId: 'chierico',
  trigger:
    'Quando qualcuno (incluso te stesso) esala il suo *ultimo respiro* in tua presenza, oppure quando spendi tempo dopo una battaglia per dedicare la vittoria alla tua divinità',
  description:
    "Se qualcuno esala l'*ultimo respiro* in tua presenza, prende +1 al tiro.\n\nInoltre, quando **spendi del tempo dopo una battaglia per dedicare la vittoria alla tua divinità e a occuparti dei morti**, prendi +1 al prossimo tiro.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

const sentiIlPopoloCantare: Move = {
  id: 'chierico.senti-il-popolo-cantare',
  name: 'Senti il Popolo Cantare?',
  type: 'starting',
  classId: 'chierico',
  trigger: 'Quando incontri persone oppresse',
  description:
    "Gli oppressi ti trattano sempre con gentilezza e rispetto, a meno che tu o la tua chiesa non abbiate dato loro un valido motivo per non farlo. D'altra parte, potrebbero anche venire a chiedere il tuo aiuto.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

const scacciareNonMorti: Move = {
  id: 'chierico.scacciare-non-morti',
  name: 'Scacciare Non Morti',
  type: 'starting',
  classId: 'chierico',
  trigger: 'Quando alzi in alto il tuo simbolo sacro e invochi la divinità per proteggerti',
  description:
    "Tira+**SAG**. Con un 7-9, nessun non morto può avvicinarsi a te. Con un 10+, inoltre, i non morti intelligenti sono temporaneamente storditi, e quelli non senzienti fuggono. Gli effetti di questa mossa terminano se ti distrai o smetti di invocare il tuo dio, oppure se un non morto che hai stordito o messo in fuga subisce un'azione aggressiva da parte tua o dei tuoi alleati. I non morti intelligenti, come vampiri e simili, riusciranno comunque a trovare il modo di nuocerti anche da lontano: sono tipi abbastanza furbi!",
  roll: {
    stat: 'saggezza',
    on10plus: {
      text: 'Nessun non morto può avvicinarsi; i non morti intelligenti sono storditi, quelli non senzienti fuggono.',
    },
    on7to9: { text: 'Nessun non morto può avvicinarsi a te.' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

/**
 * Mosse iniziali AUTOMATICHE (4): prese tutte al livello 1 senza scelta.
 * Includono *Lanciare Incantesimi Divini* (trigger per la spell-list del
 * Chierico) e *Divinità* (configurazione della Tradizione Sacra).
 */
export const chiericoStartingAutoMoves: readonly Move[] = [
  lanciareIncantesimiDivini,
  mana,
  divinita,
  assistenzaDivina,
] as const;

/**
 * Mosse iniziali di SCELTA (3): il giocatore ne sceglie UNA al livello 1
 * (`pickCount: 1`).
 */
export const chiericoStartingChoiceMoves: readonly Move[] = [
  laBilanciaDellaVitaEDellaMorte,
  sentiIlPopoloCantare,
  scacciareNonMorti,
] as const;

/** Tutte le mosse iniziali (auto + scelta). */
export const chiericoInitialMoves: readonly Move[] = [
  ...chiericoStartingAutoMoves,
  ...chiericoStartingChoiceMoves,
];
